import { Injectable } from '@angular/core';
import { Classification } from '@portal-shared/app/entities/class.model';
import { AssignmentDto } from '@services/dtos/assignment.dto';
import { AssignmentDefinition } from '@services/models/assignment-definition.model';
import { ProjectAssignmentRow } from '@shared/components/grids/rows/assignments/project-assignment-row';
import { ResourceAssignmentRow } from '@shared/components/grids/rows/assignments/resource-assignment-row';
import { PlanningRow } from '@shared/components/grids/rows/planning-row';
import { AssignmentHelper } from '@shared/helpers/assignment.helper';
import { ProjectAssignmentRowDto } from './dtos/rows/project-assignment-row.dto';
import { ResourceAssignmentRowDto } from './dtos/rows/resource-assignment-row.dto';
import { GridTransactionService } from './grid-transaction.service';

@Injectable({
  providedIn: 'root'
})
export class PlanningGridTransactionService extends GridTransactionService<PlanningRow> {
  public getOrCreateProjectAssignmentRow(
    rowDto: ProjectAssignmentRowDto,
    projectId: string,
    departmentId: string,
    classId: string
  ) {
    const assignmentDef: AssignmentDefinition = {
      projectId,
      departmentId,
      classId,
      resourceId: rowDto.resource?.id,
      requestedResourceId: rowDto.requestedResource?.id,
      fictionalResourceId: rowDto.fictionalResource?.id
    };
    let assignmentRow = this.getRowFromCache(AssignmentHelper.getRowId(assignmentDef)) as ProjectAssignmentRow;

    if (!assignmentRow) {
      const { projectId, departmentId, classId } = assignmentDef;
      assignmentRow = new ProjectAssignmentRow(
        AssignmentHelper.projectAssignmentRowDtoFactory(rowDto),
        projectId,
        departmentId,
        classId
      );
      const parentRowId = AssignmentHelper.getRowId({ projectId, departmentId, classId }, true);
      const route = this.getRowNodeFromCache(parentRowId)?.getRoute() ?? [];
      this.addRows([assignmentRow], route);
    }
    return assignmentRow;
  }

  public getOrCreateResourceAssignmentRow(
    rowDto: ResourceAssignmentRowDto,
    resourceId: string,
    flushTransaction = true
  ) {
    const assignmentDef: AssignmentDefinition = {
      projectId: rowDto.projectId,
      departmentId: rowDto.departmentId,
      classId: Classification.handleUndefinedClass(rowDto.classId),
      resourceId
    };

    let assignmentRow = this.getRowFromCache(AssignmentHelper.getRowId(assignmentDef)) as ResourceAssignmentRow;

    if (!assignmentRow) {
      assignmentRow = new ResourceAssignmentRow(AssignmentHelper.resourceAssignmentRowDtoFactory(rowDto), resourceId);
      const parentRowId = AssignmentHelper.getRowId({ resourceId });
      const route = this.getRowNodeFromCache(parentRowId)?.getRoute() ?? [];
      this.addRows([assignmentRow], route, flushTransaction);
    }
    return assignmentRow;
  }

  /**
   * Use to refresh all rows under a group (assignment definition) from the server
   * All the group hierarchy until root will be refreshed if includeHierarchy = true
   */
  public refreshRowsInGroup(groupRowId: string, includeHierarchy = false) {
    const groupRowNode = this.api?.getRowNode(groupRowId);
    const route = groupRowNode?.getRoute() ?? groupRowNode?.parent?.getRoute() ?? [];
    this.gridRefresh$.next({ route, includeHierarchy });
  }

  /**
   * Use to refresh a specific leaf row (assignment) from the server
   */
  public refreshAssignmentRow(assignment: AssignmentDto, includeHierarchy = false) {
    const rowId = AssignmentHelper.getRowId(assignment);
    const rowNode = this.api?.getRowNode(rowId);
    if (!rowNode || rowNode.group) return false; // only refresh leaf rows
    const route = rowNode.parent?.getRoute() ?? [];
    this.gridRefresh$.next({ route, assignmentId: assignment.id, includeHierarchy });
    return true;
  }
}
